// TranscribeVisit.js - Add this as a new tab in Epiphany MD
// Drop this file into your src/ directory alongside your other components

import { useState, useRef, useEffect } from "react";
import { getCurrentPracticeId } from "./lib/api";
import { getCurrentProvider } from "./lib/currentProvider";
import { db } from "./lib/db";
import { notify } from "./utils/notify";
import { todayISO } from "./utils/chartHelpers";


const CLAUDE_PROXY = "/api/claude";
const TRANSCRIBE_PROXY = "/api/transcribe-audio";

// ── System prompt that matches Rob's narrative note style ──────────────────
const SYSTEM_PROMPT = `You are an orthopedic surgeon. Convert this clinical dictation into a professional clinic note with sections: Chief Complaint, HPI, Physical Exam, Assessment, and Plan. Be concise.

OUTPUT — respond ONLY with valid JSON, no markdown:
{"note":"...","icd10":[{"code":"M75.1","description":"..."}],"cpt":[{"code":"99213","description":"..."}],"counseling":"...","treatmentPlan":"..."}`;

function trimTranscript(text) {
  if (text.length <= 1500) return { text, wasTrimmed: false };
  return {
    text: text.substring(0, 1500),
    wasTrimmed: true,
    originalLength: text.length,
  };
}

function noteAppearsIncomplete(noteText) {
  if (!noteText) return false;
  const trimmed = noteText.trim();
  if (trimmed.length < 200) return false;
  // Incomplete if last character isn't sentence-ending punctuation
  return !/[.!?'"]$/.test(trimmed);
}

export default function TranscribeVisit({ onNoteSaved }) {
  // ── State ──────────────────────────────────────────────────────────────────
  const [mode, setMode] = useState("idle"); // idle | recording | recorded | uploading | transcribing | generating | done | error
  const [transcript, setTranscript] = useState("");
  const [liveText, setLiveText] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [audioFile, setAudioFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [transcriptId, setTranscriptId] = useState(null);
  const [statusMsg, setStatusMsg] = useState("");
  const [inputMethod, setInputMethod] = useState("live"); // live | upload | paste
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [copySuccess, setCopySuccess] = useState("");
  const [patientName, setPatientName] = useState("");
  const [continuingNote, setContinuingNote] = useState(false);

  // ── Patient selector state ─────────────────────────────────────────────────
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [ptQuery, setPtQuery] = useState("");
  const [todayPatients, setTodayPatients] = useState([]);
  const [ptDropdownOpen, setPtDropdownOpen] = useState(false);
  const [contextOpen, setContextOpen] = useState(false);
  const [lastNote, setLastNote] = useState(null);
  const [saveToChartStatus, setSaveToChartStatus] = useState("idle"); // idle | saving | saved | error
  const ptSearchRef = useRef(null);

  // ── Refs ───────────────────────────────────────────────────────────────────
  const recognitionRef = useRef(null);
  const liveTextRef = useRef("");
  const timerRef = useRef(null);
  const pollRef = useRef(null);
  const fileInputRef = useRef(null);

  // ── Cleanup on unmount ─────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      clearInterval(pollRef.current);
      if (recognitionRef.current) recognitionRef.current.abort();
    };
  }, []);

  // ── Load today's scheduled patients ───────────────────────────────────────
  useEffect(() => {
    const todayStr = todayISO();
    // Two-step fetch (no embedded join — the db wrapper doesn't support
    // PostgREST joins, and appointments is moving to Cloud SQL):
    db.from("appointments")
      .select("patient_id, appointment_type, appointment_time")
      .eq("appointment_date", todayStr).eq("status", "scheduled")
      .order("appointment_time", { ascending: true })
      .then(async ({ data }) => {
        if (!data || data.length === 0) { setTodayPatients([]); return; }
        const ids = [...new Set(data.map(a => a.patient_id).filter(Boolean))];
        const { data: patientRows } = await db.from("patients")
          .select("id, first_name, last_name, date_of_birth")
          .in("id", ids);
        const byId = Object.fromEntries((patientRows || []).map(p => [p.id, p]));
        const seen = new Set();
        const pts = [];
        data.forEach(a => {
          if (seen.has(a.patient_id)) return;
          seen.add(a.patient_id);
          const p = byId[a.patient_id];
          if (p) {
            pts.push({
              id: a.patient_id,
              first_name: p.first_name,
              last_name: p.last_name,
              date_of_birth: p.date_of_birth,
              apptTime: a.appointment_time,
              apptType: a.appointment_type,
            });
          }
        });
        setTodayPatients(pts);
      })
      .catch(() => setTodayPatients([]));
  }, []);

  // ── Load patient context (last note) when patient selected ────────────────
  useEffect(() => {
    if (!selectedPatient) { setLastNote(null); return; }
    db.from("clinical_notes").select("body, note_type, created_at").eq("patient_id", selectedPatient.id)
      .order("created_at", { ascending: false }).limit(1)
      .then(({ data }) => setLastNote(data?.[0] || null));
  }, [selectedPatient]);

  // ── Live Recording (Web Speech API) ───────────────────────────────────────
  const startRecording = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setError("Live recording requires Chrome or Edge browser. Please use audio file upload instead.");
      setMode("error");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";
    recognitionRef.current = recognition;
    liveTextRef.current = "";
    setLiveText("");
    setRecordingSeconds(0);

    timerRef.current = setInterval(() => {
      setRecordingSeconds(s => s + 1);
    }, 1000);

    recognition.onresult = (e) => {
      let interim = "";
      let final = liveTextRef.current;
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) {
          final += e.results[i][0].transcript + " ";
        } else {
          interim += e.results[i][0].transcript;
        }
      }
      liveTextRef.current = final;
      setLiveText(final + interim);
    };

    recognition.onerror = (e) => {
      if (e.error !== "aborted") {
        setError(`Microphone error: ${e.error}. Please check browser permissions.`);
        setMode("error");
      }
      clearInterval(timerRef.current);
    };

    recognition.onend = () => {
      clearInterval(timerRef.current);
    };

    recognition.start();
    setMode("recording");
  };

  const stopRecording = () => {
    if (recognitionRef.current) recognitionRef.current.stop();
    clearInterval(timerRef.current);
    const finalText = liveTextRef.current.trim();
    setTranscript(finalText);
    setLiveText(finalText);
    setMode("recorded");
  };

  // ── Audio File Upload → AssemblyAI ─────────────────────────────────────────
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAudioFile(file);
    setFileName(file.name);
    setMode("recorded");
    setTranscript("");
  };

  const uploadAndTranscribe = async () => {
    if (!audioFile) return;
    setMode("uploading");
    setStatusMsg("Uploading audio to AssemblyAI...");

    try {
      const arrayBuffer = await audioFile.arrayBuffer();
      const base64 = btoa(String.fromCharCode(...new Uint8Array(arrayBuffer)));

      const uploadRes = await fetch(TRANSCRIBE_PROXY, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "upload", audioData: base64 }),
      });
      const uploadData = await uploadRes.json();
      if (!uploadData.transcriptId) throw new Error("Upload failed");

      setTranscriptId(uploadData.transcriptId);
      setMode("transcribing");
      setStatusMsg("Transcribing with AssemblyAI (medical vocabulary)...");

      // Poll every 3 seconds
      pollRef.current = setInterval(async () => {
        const pollRes = await fetch(TRANSCRIBE_PROXY, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "poll", transcriptId: uploadData.transcriptId }),
        });
        const pollData = await pollRes.json();

        if (pollData.status === "completed") {
          clearInterval(pollRef.current);
          setTranscript(pollData.transcript);
          setMode("recorded");
          setStatusMsg("");
        } else if (pollData.status === "error") {
          clearInterval(pollRef.current);
          throw new Error("Transcription failed on AssemblyAI");
        } else {
          setStatusMsg(`Transcribing... (${pollData.status})`);
        }
      }, 3000);

    } catch (err) {
      setError(err.message);
      setMode("error");
    }
  };

  // ── Generate Note via Claude (streaming) ──────────────────────────────────
  const generateNote = async () => {
    if (!transcript.trim()) {
      setError("No transcript available. Please record, upload, or paste a conversation first.");
      return;
    }

    const { text: trimmedText, wasTrimmed, originalLength } = trimTranscript(transcript);

    setMode("generating");
    setStatusMsg(
      wasTrimmed
        ? `Transcript trimmed from ${originalLength} to 1500 characters — generating note...`
        : `Generating note…`
    );

    try {
      const payload = {
        model: "claude-haiku-4-5-20251001",
        max_tokens: 1000,
        system: SYSTEM_PROMPT,
        messages: [
          {
            role: "user",
            content: `Please analyze this patient-doctor conversation and generate a complete clinic note with ICD-10 codes, CPT codes, counseling summary, and treatment plan.\n\nTRANSCRIPT:\n${trimmedText}`,
          },
        ],
      };
      const response = await fetch(CLAUDE_PROXY, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errText = await response.text();
        console.log("Full error response:", errText);
        throw new Error(`Server error: ${response.status} — ${errText.substring(0, 200)}`);
      }

      const data = await response.json();
      const raw = data.content?.[0]?.text || "";
      const clean = raw.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      setResult(parsed);
      setMode("done");
      setStatusMsg("");
      if (onNoteSaved) {
        const now = new Date();
        const dateTime = now.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })
          + " " + now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
        onNoteSaved({
          id: Date.now().toString(),
          dateTime,
          patientName: selectedPatient ? `${selectedPatient.first} ${selectedPatient.last}` : patientName.trim(),
          visitType: "",
          note: parsed.note,
          counseling: parsed.counseling,
          treatmentPlan: parsed.treatmentPlan,
          icd10: parsed.icd10,
          cpt: parsed.cpt,
        });
      }
    } catch (err) {
      setError(`Failed to generate note: ${err.message}`);
      setMode("error");
    }
  };

  // ── Continue truncated note ────────────────────────────────────────────────
  const continueNote = async () => {
    setContinuingNote(true);
    try {
      const response = await fetch(CLAUDE_PROXY, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001",
          max_tokens: 800,
          messages: [
            {
              role: "user",
              content: `The following clinic note was cut off before completion. Continue writing it from exactly where it left off, completing all remaining sections. Write ONLY the continuation — do not repeat text already written. End with return precautions and follow-up interval.\n\nPARTIAL NOTE:\n${result.note}`,
            },
          ],
        }),
      });
      const data = await response.json();
      const continuation = data.content?.[0]?.text?.trim() || "";
      if (continuation) {
        setResult(prev => ({ ...prev, note: prev.note.trimEnd() + " " + continuation }));
      }
    } catch (err) {
      setError("Failed to continue note generation. Please try again.");
    }
    setContinuingNote(false);
  };

  // ── Copy / Download helpers ────────────────────────────────────────────────
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(label);
      setTimeout(() => setCopySuccess(""), 2000);
    });
  };

  const downloadNote = () => {
    if (!result) return;
    const content = [
      "CLINIC NOTE",
      "═".repeat(60),
      result.note,
      "",
      "COUNSELING SUMMARY",
      "═".repeat(60),
      result.counseling,
      "",
      "TREATMENT PLAN",
      "═".repeat(60),
      result.treatmentPlan,
      "",
      "ICD-10 CODES",
      "═".repeat(60),
      ...(result.icd10 || []).map(c => `${c.code}  –  ${c.description}`),
      "",
      "CPT CODES",
      "═".repeat(60),
      ...(result.cpt || []).map(c => `${c.code}  –  ${c.description}`),
    ].join("\n");

    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `clinic-note-${todayISO()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const reset = () => {
    setMode("idle");
    setTranscript("");
    setLiveText("");
    setResult(null);
    setError("");
    setAudioFile(null);
    setFileName("");
    setStatusMsg("");
    setRecordingSeconds(0);
    setPatientName("");
    setSaveToChartStatus("idle");
    liveTextRef.current = "";
    clearInterval(timerRef.current);
    clearInterval(pollRef.current);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ── Save to Chart ──────────────────────────────────────────────────────────
  const saveToChart = async () => {
    if (!result) return;
    if (!selectedPatient) {
      if (!window.confirm("No patient selected — save as unassigned note?")) return;
    }
    setSaveToChartStatus("saving");

    // Concatenate all sections into body
    const bodyParts = [result.note];
    if (result.counseling)    bodyParts.push("\n\nCOUNSELING SUMMARY\n" + result.counseling);
    if (result.treatmentPlan) bodyParts.push("\n\nTREATMENT PLAN\n"     + result.treatmentPlan);

    // Map code arrays to {code, desc} shape used by clinical_notes
    const icd10Codes = (result.icd10 || []).map(c => ({ code: c.code, desc: c.description }));
    const cptCodes   = (result.cpt   || []).map(c => ({ code: c.code, desc: c.description }));

    // Resolved, never defaulted — same rule as the note editor and mobile's
    // pipeline.js resolveProvider(). This page hardcoded "Dr. Walker", so every
    // note saved from it carried that name regardless of who was signed in.
    const cp = await getCurrentProvider();
    const providerName = cp
      ? `Dr. ${`${cp.first_name || ''} ${cp.last_name || ''}`.trim()}`.trim()
      : '';
    if (!providerName) {
      setSaveToChartStatus("error");
      notify("Couldn't resolve your provider record — sign out and back in, or contact your admin.", 'error');
      return;
    }
    const practiceId = await getCurrentPracticeId();
    const now = new Date();
    const payload = {
      patient_id:  selectedPatient?.id || null,
      body:        bodyParts.join(""),
      note_type:   "Visit Note",
      provider:    providerName,
      visit_date:  todayISO(), // was now.toISOString() — UTC, filed evening notes on tomorrow
      visit_time:  now.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false }).slice(0, 5),
      status:      "draft",
      icd10_codes: icd10Codes,
      cpt_codes:   cptCodes,
      practice_id: practiceId || null,
    };
    const { error: err } = await db.from("clinical_notes").insert([payload]);
    setSaveToChartStatus(err ? "error" : "saved");
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  // ── Styles ─────────────────────────────────────────────────────────────────
  const styles = {
    container: {
      fontFamily: "'Georgia', serif",
      maxWidth: "900px",
      margin: "0 auto",
      padding: "24px 20px",
      color: "var(--text-1)",
    },
    header: {
      fontSize: "22px",
      fontWeight: "700",
      color: "var(--text-1)",
      marginBottom: "4px",
      letterSpacing: "-0.3px",
    },
    subheader: {
      fontSize: "14px",
      color: "var(--text-3)",
      marginBottom: "24px",
      fontFamily: "'Helvetica Neue', sans-serif",
    },
    card: {
      background: "var(--navy-3)",
      border: "1px solid var(--border)",
      borderRadius: "12px",
      padding: "20px",
      marginBottom: "16px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
    },
    sectionTitle: {
      fontSize: "11px",
      fontWeight: "700",
      letterSpacing: "1.2px",
      textTransform: "uppercase",
      color: "var(--text-3)",
      marginBottom: "12px",
      fontFamily: "'Helvetica Neue', sans-serif",
    },
    tabRow: {
      display: "flex",
      gap: "8px",
      marginBottom: "16px",
    },
    tab: (active) => ({
      padding: "8px 16px",
      borderRadius: "8px",
      border: `1px solid ${active ? "var(--teal)" : "var(--border-2)"}`,
      background: active ? "var(--teal-lt)" : "var(--navy-4)",
      color: active ? "var(--teal)" : "var(--text-3)",
      cursor: "pointer",
      fontSize: "13px",
      fontFamily: "'Helvetica Neue', sans-serif",
      fontWeight: active ? "600" : "400",
      transition: "all 0.15s",
    }),
    recordBtn: (recording) => ({
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "14px 28px",
      borderRadius: "10px",
      border: "none",
      background: recording ? "#dc2626" : "#0f3460",
      color: "#fff",
      fontSize: "15px",
      fontFamily: "'Helvetica Neue', sans-serif",
      fontWeight: "600",
      cursor: "pointer",
      transition: "all 0.2s",
    }),
    generateBtn: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "14px 32px",
      borderRadius: "10px",
      border: "none",
      background: "linear-gradient(135deg, #0f3460 0%, #16213e 100%)",
      color: "#fff",
      fontSize: "15px",
      fontFamily: "'Helvetica Neue', sans-serif",
      fontWeight: "600",
      cursor: "pointer",
      width: "100%",
      justifyContent: "center",
      marginTop: "8px",
    },
    textarea: {
      width: "100%",
      minHeight: "140px",
      padding: "12px",
      borderRadius: "8px",
      border: "1px solid var(--border)",
      background: "var(--navy-4)",
      fontFamily: "'Georgia', serif",
      fontSize: "14px",
      lineHeight: "1.6",
      color: "var(--text-1)",
      resize: "vertical",
      outline: "none",
      boxSizing: "border-box",
    },
    noteText: {
      fontFamily: "'Georgia', serif",
      fontSize: "14px",
      lineHeight: "1.8",
      color: "var(--text-1)",
      whiteSpace: "pre-wrap",
    },
    codeRow: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      padding: "10px 12px",
      borderRadius: "8px",
      background: "var(--navy-4)",
      marginBottom: "6px",
      fontFamily: "'Helvetica Neue', sans-serif",
    },
    codeBadge: (type) => ({
      padding: "3px 10px",
      borderRadius: "20px",
      fontSize: "12px",
      fontWeight: "700",
      background: type === "icd" ? "rgba(59,130,246,0.15)" : "rgba(34,197,94,0.12)",
      color: type === "icd" ? "#60a5fa" : "#4ade80",
      minWidth: "80px",
      textAlign: "center",
    }),
    actionRow: {
      display: "flex",
      gap: "10px",
      marginTop: "16px",
      flexWrap: "wrap",
    },
    actionBtn: (variant) => ({
      padding: "9px 18px",
      borderRadius: "8px",
      border: `1px solid ${variant === "primary" ? "var(--teal)" : "var(--border-2)"}`,
      background: variant === "primary" ? "var(--teal)" : "var(--navy-4)",
      color: "#fff",
      fontSize: "13px",
      fontFamily: "'Helvetica Neue', sans-serif",
      fontWeight: "600",
      cursor: "pointer",
    }),
    statusBadge: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      padding: "10px 16px",
      borderRadius: "8px",
      background: "var(--navy-4)",
      color: "var(--teal)",
      border: "1px solid var(--border-2)",
      fontSize: "13px",
      fontFamily: "'Helvetica Neue', sans-serif",
      marginBottom: "12px",
    },
    uploadArea: {
      border: "2px dashed var(--border-2)",
      borderRadius: "10px",
      padding: "32px",
      textAlign: "center",
      cursor: "pointer",
      transition: "all 0.2s",
    },
    errorBox: {
      padding: "14px 16px",
      borderRadius: "8px",
      background: "rgba(220,38,38,0.1)",
      border: "1px solid rgba(220,38,38,0.3)",
      color: "#f87171",
      fontSize: "13px",
      fontFamily: "'Helvetica Neue', sans-serif",
      marginBottom: "12px",
    },
    pulse: {
      width: "10px",
      height: "10px",
      borderRadius: "50%",
      background: "#dc2626",
      animation: "pulse 1s infinite",
    },
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={styles.container}>
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes spin { to{transform:rotate(360deg)} }
        .upload-area:hover { border-color: #0f3460 !important; background: #f8fafc; }
      `}</style>

      <div style={styles.header}>🎙 Visit Transcription</div>
      <div style={styles.subheader}>Record or upload a patient visit → auto-generate clinic note, ICD-10 & CPT codes</div>

      {/* ── Patient Selector ── */}
      <div style={styles.card}>
        <div style={styles.sectionTitle}>Associate with Patient</div>
        <div style={{ position: "relative" }}>
          <input
            ref={ptSearchRef}
            value={ptQuery}
            onChange={e => { setPtQuery(e.target.value); setPtDropdownOpen(true); if (!e.target.value) setSelectedPatient(null); }}
            onFocus={() => setPtDropdownOpen(true)}
            placeholder="Search today's patients or type a name…"
            style={{ ...styles.textarea, minHeight: "unset", padding: "10px 14px", fontSize: 13, fontFamily: "'Helvetica Neue', sans-serif" }}
          />
          {ptDropdownOpen && ptQuery.length === 0 && (
            <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 50, maxHeight: 200, overflowY: "auto", marginTop: 4 }}>
              <div style={{ padding: "6px 12px 4px", fontSize: 11, fontWeight: 700, letterSpacing: "0.8px", textTransform: "uppercase", color: "#94a3b8", fontFamily: "sans-serif" }}>Today's Schedule</div>
              {todayPatients.length === 0 && (
                <div style={{ padding: "9px 14px", color: "#94a3b8", fontSize: 13, fontFamily: "sans-serif" }}>No patients scheduled today</div>
              )}
              {todayPatients.map(p => (
                <div key={p.id} style={{ padding: "9px 14px", cursor: "pointer", fontSize: 13, color: "#334155", display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: "sans-serif" }}
                  onMouseDown={() => { setSelectedPatient(p); setPtQuery(`${p.first_name} ${p.last_name}`); setPtDropdownOpen(false); setSaveToChartStatus("idle"); }}>
                  <span><strong>{p.first_name} {p.last_name}</strong>{p.apptTime ? <span style={{ color: "#94a3b8", marginLeft: 8 }}>{p.apptTime} · {p.apptType}</span> : null}</span>
                </div>
              ))}
            </div>
          )}
          {ptDropdownOpen && ptQuery.length >= 1 && (
            <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", zIndex: 50, maxHeight: 200, overflowY: "auto", marginTop: 4 }}>
              {todayPatients.filter(p => `${p.first_name} ${p.last_name}`.toLowerCase().includes(ptQuery.toLowerCase())).map(p => (
                <div key={p.id} style={{ padding: "9px 14px", cursor: "pointer", fontSize: 13, color: "#334155", fontFamily: "sans-serif" }}
                  onMouseDown={() => { setSelectedPatient(p); setPtQuery(`${p.first_name} ${p.last_name}`); setPtDropdownOpen(false); setSaveToChartStatus("idle"); }}>
                  <strong>{p.first_name} {p.last_name}</strong>
                  <span style={{ color: "#94a3b8", marginLeft: 8 }}>DOB {p.date_of_birth}</span>
                </div>
              ))}
              {todayPatients.filter(p => `${p.first_name} ${p.last_name}`.toLowerCase().includes(ptQuery.toLowerCase())).length === 0 && (
                <div style={{ padding: "9px 14px", color: "#94a3b8", fontSize: 13, fontFamily: "sans-serif" }}>No patients found</div>
              )}
            </div>
          )}
        </div>

        {selectedPatient && (
          <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 20, padding: "5px 12px", fontSize: 13, fontFamily: "sans-serif", color: "#1d4ed8" }}>
              <span>👤</span>
              <span><strong>{selectedPatient.first_name} {selectedPatient.last_name}</strong> · DOB {selectedPatient.date_of_birth}</span>
              <button onClick={() => { setSelectedPatient(null); setPtQuery(""); setSaveToChartStatus("idle"); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b", fontSize: 14, lineHeight: 1, padding: 0 }}>✕</button>
            </div>
            <button onClick={() => setContextOpen(o => !o)} style={{ background: "none", border: "1px solid #e2e8f0", borderRadius: 8, padding: "5px 10px", fontSize: 12, cursor: "pointer", color: "#64748b", fontFamily: "sans-serif" }}>
              {contextOpen ? "▲ Hide context" : "▼ Show chart context"}
            </button>
          </div>
        )}

        {selectedPatient && contextOpen && (
          <div style={{ marginTop: 12, background: "#f8fafc", borderRadius: 8, padding: "12px 14px", fontSize: 13, fontFamily: "sans-serif", color: "#334155" }}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontWeight: 700, fontSize: 11, letterSpacing: "0.8px", textTransform: "uppercase", color: "#94a3b8" }}>Active Problems</span>
              <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 6 }}>
                {(selectedPatient.problems || []).length > 0
                  ? selectedPatient.problems.map((pr, i) => (
                    <span key={i} style={{ background: "#fee2e2", color: "#dc2626", borderRadius: 12, padding: "2px 10px", fontSize: 12 }}>{pr}</span>
                  ))
                  : <span style={{ color: "#94a3b8" }}>None on record</span>
                }
              </div>
            </div>
            <div>
              <span style={{ fontWeight: 700, fontSize: 11, letterSpacing: "0.8px", textTransform: "uppercase", color: "#94a3b8" }}>Last Note</span>
              {lastNote ? (
                <div style={{ marginTop: 4, color: "#475569", lineHeight: 1.6, maxHeight: 120, overflowY: "auto" }}>
                  <span style={{ fontSize: 11, color: "#94a3b8" }}>{lastNote.note_type} · {new Date(lastNote.created_at).toLocaleDateString()}</span>
                  <div style={{ marginTop: 4 }}>{lastNote.body?.slice(0, 400)}{lastNote.body?.length > 400 ? "…" : ""}</div>
                </div>
              ) : (
                <div style={{ marginTop: 4, color: "#94a3b8" }}>No previous notes found</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Error ── */}
      {mode === "error" && (
        <div style={styles.errorBox}>
          ⚠️ {error}
          <button onClick={reset} style={{ marginLeft: "12px", textDecoration: "underline", background: "none", border: "none", color: "#dc2626", cursor: "pointer" }}>Reset</button>
        </div>
      )}

      {/* ── Input Method Tabs ── */}
      {mode === "idle" || mode === "recording" || mode === "recorded" ? (
        <div style={styles.card}>
          <div style={styles.sectionTitle}>Input Method</div>
          <div style={styles.tabRow}>
            {[["live", "🎤 Live Recording"], ["upload", "📁 Upload Audio"], ["paste", "📋 Paste Transcript"]].map(([val, label]) => (
              <button key={val} style={styles.tab(inputMethod === val)} onClick={() => { setInputMethod(val); reset(); setMode("idle"); }}>
                {label}
              </button>
            ))}
          </div>

          {/* Live Recording */}
          {inputMethod === "live" && (
            <div>
              {mode !== "recording" ? (
                <button style={styles.recordBtn(false)} onClick={startRecording}>
                  <span>🎤</span> Start Recording Visit
                </button>
              ) : (
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "14px" }}>
                    <button style={styles.recordBtn(true)} onClick={stopRecording}>
                      <span style={styles.pulse} /> Stop Recording
                    </button>
                    <span style={{ fontFamily: "monospace", fontSize: "18px", color: "#dc2626", fontWeight: "700" }}>
                      {formatTime(recordingSeconds)}
                    </span>
                  </div>
                  <div style={{ ...styles.textarea, minHeight: "120px", overflowY: "auto", background: "#fafafa", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", fontFamily: "'Georgia', serif", fontSize: "14px", lineHeight: "1.6" }}>
                    {liveText || <span style={{ color: "#94a3b8" }}>Listening... speak clearly near your microphone.</span>}
                  </div>
                </div>
              )}
              {mode === "recorded" && transcript && (
                <div style={{ marginTop: "14px" }}>
                  <div style={styles.sectionTitle}>Recorded Transcript</div>
                  <textarea
                    style={styles.textarea}
                    value={transcript}
                    onChange={e => setTranscript(e.target.value)}
                  />
                  <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "4px", fontFamily: "sans-serif" }}>
                    You can edit the transcript above before generating the note.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* File Upload */}
          {inputMethod === "upload" && (
            <div>
              <div
                className="upload-area"
                style={styles.uploadArea}
                onClick={() => fileInputRef.current?.click()}
              >
                <div style={{ fontSize: "32px", marginBottom: "8px" }}>🎵</div>
                <div style={{ fontFamily: "'Helvetica Neue', sans-serif", fontWeight: "600", color: "#334155", marginBottom: "4px" }}>
                  {fileName || "Click to select audio file"}
                </div>
                <div style={{ fontSize: "12px", color: "#94a3b8", fontFamily: "sans-serif" }}>
                  MP3, MP4, WAV, M4A, WEBM supported
                </div>
                <input ref={fileInputRef} type="file" accept="audio/*,video/mp4,video/webm" style={{ display: "none" }} onChange={handleFileSelect} />
              </div>

              {audioFile && mode === "recorded" && !transcript && (
                <button style={{ ...styles.generateBtn, marginTop: "12px", background: "linear-gradient(135deg, #059669 0%, #047857 100%)" }} onClick={uploadAndTranscribe}>
                  🔬 Transcribe with AssemblyAI
                </button>
              )}

              {(mode === "uploading" || mode === "transcribing") && (
                <div style={{ ...styles.statusBadge, marginTop: "12px" }}>
                  <span style={{ display: "inline-block", width: "14px", height: "14px", border: "2px solid #1d4ed8", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
                  {statusMsg}
                </div>
              )}

              {transcript && mode === "recorded" && (
                <div style={{ marginTop: "14px" }}>
                  <div style={styles.sectionTitle}>Transcribed Conversation</div>
                  <textarea style={styles.textarea} value={transcript} onChange={e => setTranscript(e.target.value)} />
                  <div style={{ fontSize: "12px", color: "#94a3b8", marginTop: "4px", fontFamily: "sans-serif" }}>
                    Speaker labels: Doctor / Patient. Edit above if needed.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Paste Transcript */}
          {inputMethod === "paste" && (
            <div>
              <textarea
                style={styles.textarea}
                placeholder="Paste the patient-doctor conversation here. You can label speakers as 'Doctor:' and 'Patient:' for best results, or just paste raw text..."
                value={transcript}
                onChange={e => { setTranscript(e.target.value); if (e.target.value) setMode("recorded"); }}
              />
            </div>
          )}

          {/* Generate Button */}
          {(mode === "recorded" && transcript) && (
            <div style={{ marginTop: 12 }}>
              {!selectedPatient && (
                <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#92400e", fontFamily: "sans-serif", marginBottom: 8 }}>
                  ⚠️ No patient selected — you can still generate a note, but it won't be linked to a chart.
                </div>
              )}
              <button style={styles.generateBtn} onClick={generateNote}>
                ⚕️ Generate Clinic Note + Codes
              </button>
            </div>
          )}
        </div>
      ) : null}

      {/* ── Generating spinner ── */}
      {mode === "generating" && (
        <div style={styles.card}>
          <div style={styles.statusBadge}>
            <span style={{ display: "inline-block", width: "14px", height: "14px", border: "2px solid #1d4ed8", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            {statusMsg}
          </div>
          <div style={{ fontSize: "13px", color: "#94a3b8", fontFamily: "sans-serif", marginTop: "6px" }}>
            Analyzing transcript and writing the complete note — longer visits may take 20–40 seconds.
          </div>
        </div>
      )}

      {/* ── Results ── */}
      {mode === "done" && result && (
        <div>
          {/* Clinic Note */}
          <div style={styles.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
              <div style={styles.sectionTitle}>Clinic Note</div>
              <button style={styles.actionBtn("secondary")} onClick={() => copyToClipboard(result.note, "note")}>
                {copySuccess === "note" ? "✓ Copied!" : "Copy"}
              </button>
            </div>
            <div style={styles.noteText}>{result.note}</div>
          </div>

          {/* Continue Note if truncated */}
          {noteAppearsIncomplete(result.note) && (
            <div style={{ ...styles.card, background: "#fffbeb", border: "1px solid #fde68a" }}>
              <div style={{ fontSize: "13px", color: "#92400e", fontFamily: "sans-serif", marginBottom: "10px" }}>
                ⚠️ The note may be incomplete — it appears to end mid-sentence. Click below to continue generating from where it left off.
              </div>
              <button
                style={{ ...styles.actionBtn("primary"), background: continuingNote ? "#d97706" : "#b45309", border: "none", cursor: continuingNote ? "wait" : "pointer" }}
                onClick={continueNote}
                disabled={continuingNote}
              >
                {continuingNote ? (
                  <>
                    <span style={{ display: "inline-block", width: "12px", height: "12px", border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite", marginRight: "8px" }} />
                    Continuing note…
                  </>
                ) : "↩ Continue Note"}
              </button>
            </div>
          )}

          {/* Counseling */}
          {result.counseling && (
            <div style={styles.card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                <div style={styles.sectionTitle}>Counseling Summary</div>
                <button style={styles.actionBtn("secondary")} onClick={() => copyToClipboard(result.counseling, "counseling")}>
                  {copySuccess === "counseling" ? "✓ Copied!" : "Copy"}
                </button>
              </div>
              <div style={styles.noteText}>{result.counseling}</div>
            </div>
          )}

          {/* Treatment Plan */}
          {result.treatmentPlan && (
            <div style={styles.card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px" }}>
                <div style={styles.sectionTitle}>Treatment Plan</div>
                <button style={styles.actionBtn("secondary")} onClick={() => copyToClipboard(result.treatmentPlan, "plan")}>
                  {copySuccess === "plan" ? "✓ Copied!" : "Copy"}
                </button>
              </div>
              <div style={styles.noteText}>{result.treatmentPlan}</div>
            </div>
          )}

          {/* Codes */}
          <div style={styles.card}>
            <div style={styles.sectionTitle}>ICD-10 Diagnosis Codes</div>
            {(result.icd10 || []).map((c, i) => (
              <div key={i} style={styles.codeRow}>
                <span style={styles.codeBadge("icd")}>{c.code}</span>
                <span style={{ fontSize: "14px", color: "#334155" }}>{c.description}</span>
              </div>
            ))}
            <div style={{ ...styles.sectionTitle, marginTop: "16px" }}>CPT Procedure Codes</div>
            {(result.cpt || []).map((c, i) => (
              <div key={i} style={styles.codeRow}>
                <span style={styles.codeBadge("cpt")}>{c.code}</span>
                <span style={{ fontSize: "14px", color: "#334155" }}>{c.description}</span>
              </div>
            ))}
            <button style={styles.actionBtn("secondary")} onClick={() => {
              const text = [...(result.icd10||[]).map(c=>`ICD-10: ${c.code} – ${c.description}`), ...(result.cpt||[]).map(c=>`CPT: ${c.code} – ${c.description}`)].join("\n");
              copyToClipboard(text, "codes");
            }}>
              {copySuccess === "codes" ? "✓ Copied!" : "Copy All Codes"}
            </button>
          </div>

          {/* Save to Chart */}
          <div style={{ ...styles.card, background: saveToChartStatus === "saved" ? "#f0fdf4" : saveToChartStatus === "error" ? "#fef2f2" : "#eff6ff", border: `1px solid ${saveToChartStatus === "saved" ? "#bbf7d0" : saveToChartStatus === "error" ? "#fecaca" : "#bfdbfe"}` }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
              <div style={{ fontFamily: "sans-serif", fontSize: 13 }}>
                {saveToChartStatus === "saved" ? (
                  <span style={{ color: "#15803d", fontWeight: 600 }}>✓ Saved to chart{selectedPatient ? ` for ${selectedPatient.first} ${selectedPatient.last}` : " (unassigned)"}</span>
                ) : saveToChartStatus === "error" ? (
                  <span style={{ color: "#dc2626" }}>⚠️ Failed to save — check Supabase connection</span>
                ) : (
                  <span style={{ color: "#1d4ed8" }}>
                    {selectedPatient ? `💾 Save to ${selectedPatient.first} ${selectedPatient.last}'s chart` : "💾 Save note to chart (no patient selected)"}
                  </span>
                )}
              </div>
              {saveToChartStatus !== "saved" && (
                <button
                  style={{ ...styles.actionBtn("primary"), background: "#1d4ed8", border: "none", opacity: saveToChartStatus === "saving" ? 0.7 : 1 }}
                  onClick={saveToChart} disabled={saveToChartStatus === "saving"}>
                  {saveToChartStatus === "saving" ? "Saving…" : "Save to Chart"}
                </button>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div style={styles.actionRow}>
            <button style={styles.actionBtn("primary")} onClick={downloadNote}>⬇ Download Note (.txt)</button>
            <button style={styles.actionBtn("secondary")} onClick={() => copyToClipboard(
              `${result.note}\n\n${result.counseling}\n\n${result.treatmentPlan}`, "all"
            )}>{copySuccess === "all" ? "✓ Copied!" : "Copy Full Note"}</button>
            <button style={styles.actionBtn("secondary")} onClick={reset}>+ New Visit</button>
          </div>
        </div>
      )}
    </div>
  );
}
